import { JSX, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  withLDProvider,
  useLDClient,
  useFlags,
} from 'launchdarkly-react-client-sdk';

// Selectors
import { selectAgent } from 'app/modules/session/selectors';

// Actions
import { setFeatureFlags } from 'app/shared/featureFlags/actions';

// Constants
import getFromProcessEnv from 'app/shared/utils/getFromProcessEnv';
import {
  FeatureFlags,
  DEFAULT_FEATURE_FLAGS,
} from 'app/shared/featureFlags/models';

const LUMOS_URL = getFromProcessEnv('REACT_APP_AUTH0_LOGIN_REDIRECT_URL');

interface Props {
  children: ReactNode;
}

const FeatureFlagProvider = (props: Props) => {
  const { children } = props;
  const loggedAgent = useSelector(selectAgent);
  const launchDarklyClient = useLDClient();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (launchDarklyClient && loggedAgent.email) {
      const initialize = async () => {
        try {
          await launchDarklyClient.waitForInitialization(4);
          await launchDarklyClient.identify({
            key: loggedAgent.email,
            email: loggedAgent.email,
            custom: {
              org_id: loggedAgent.org_id,
              org_name: loggedAgent.org_name,
              lumos_url: LUMOS_URL,
            },
          });
        } catch {
        } finally {
          // even if initialization fails, we still want the user to be able to use
          // the app so set initialized to true - they just may have incorrect flags
          setInitialized(true);
        }
      };
      initialize();
    }
  }, [
    launchDarklyClient,
    loggedAgent.email,
    loggedAgent.org_id,
    loggedAgent.org_name,
  ]);

  return (
    <>
      {initialized && <FeatureFlagConsumer />}
      {children}
    </>
  );
};

// set feature flags to store
// defined as a separate component instead of in FeatureFlagProvider to only call
// useFlags once LD is initialized to avoid unnecessary monitoring evaluations in LD
const FeatureFlagConsumer = () => {
  const dispatch = useDispatch();
  const flags = useFlags<FeatureFlags>();
  useEffect(() => {
    dispatch(setFeatureFlags({ ...flags }));
  }, [dispatch, flags]);
  return null;
};

// https://github.com/launchdarkly/react-client-sdk/issues/223#issuecomment-2701308189
export default withLDProvider<JSX.IntrinsicAttributes & Props>({
  clientSideID: getFromProcessEnv('LAUNCH_DARKLY_CLIENT_ID'),
  context: {
    anonymous: true,
    custom: {
      lumos_url: getFromProcessEnv('REACT_APP_AUTH0_LOGIN_REDIRECT_URL'),
    },
  },
  // only subscribe to the defined feature flags
  flags: DEFAULT_FEATURE_FLAGS,
  options: { useReport: true, allAttributesPrivate: true },
  reactOptions: { useCamelCaseFlagKeys: false },
})(FeatureFlagProvider);
